﻿import { useQuery } from "@tanstack/react-query";
import axios from "../../../api/axios";
import {
  manproQueryKeys,
  providerProjectService,
  type PaginatedResponse,
} from "../../../api/manpro";
import projectService from "../../../api/projectService";
import type {
  Identifier,
  ProviderCard,
  Technician,
  VendorItem,
  ProjectListItem,
  ProjectTree,
  ArchitectVendorResponse,
} from "../types";

export const projectManagementQueryKeys = {
  providers: ["project-management", "providers"] as const,
  technicians: ["project-management", "technicians"] as const,
  vendors: ["project-management", "vendors"] as const,
  projects: (categoryId?: Identifier | null) =>
    ["project-management", "projects", categoryId ?? "all"] as const,
  ownerProjectBids: (projectId: string) =>
    ["project-management", "owner-bids", projectId] as const,
  projectTree: (projectId: Identifier | null) =>
    ["project-management", "project-tree", projectId ?? "none"] as const,
};

export function useProjectQueries({
  userRole,
  categoryId,
  activeProjectId,
  isVendorModalOpen,
}: {
  userRole?: string;
  categoryId: string | null;
  activeProjectId: Identifier | null;
  isVendorModalOpen: boolean;
}) {
  const activeProjectQuery = useQuery({
    queryKey: projectManagementQueryKeys.projectTree(activeProjectId),
    queryFn: async () =>
      (await projectService.getProjectTree(activeProjectId as Identifier)) as ProjectTree | null,
    enabled: Boolean(activeProjectId),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
  });

  const providersQuery = useQuery({
    queryKey: projectManagementQueryKeys.providers,
    queryFn: async () => {
      const response = await axios.get(
        "/v1/admin/facility/technician/available-users?search=provider",
      );
      const rawProviders = (response.data.content?.data || []) as Technician[];
      return rawProviders.map((u) => ({
        id: u.id,
        name: `${u.first_name} ${u.last_name || ""}`.trim(),
        location: u.phone || "Lokasi tidak set",
        modules:
          u.roles?.map((r: { role_name: string }) =>
            r.role_name.replace("user_", ""),
          ) || ["general"],
        raw: u,
      })) as ProviderCard[];
    },
    staleTime: 5 * 60_000,
  });

  const vendorsQuery = useQuery({
    queryKey: projectManagementQueryKeys.vendors,
    queryFn: async () => {
      const content = (await projectService.getVendors()) as ArchitectVendorResponse;
      return (content.data || []) as VendorItem[];
    },
    enabled: isVendorModalOpen,
    staleTime: 5 * 60_000,
  });

  const projectsQuery = useQuery({
    queryKey: projectManagementQueryKeys.projects(categoryId),
    queryFn: async () => {
      const projectsData = (await projectService.getProjects(
        categoryId,
      )) as ProjectListItem[] | { data?: ProjectListItem[] };
      return Array.isArray(projectsData) ? projectsData : projectsData?.data || [];
    },
    enabled: userRole !== "provider",
    staleTime: 60_000,
  });

  const providerManproProjectsQuery = useQuery({
    queryKey: manproQueryKeys.providerProjects({ limit: 50 }),
    queryFn: () => providerProjectService.getProviderProjects({ limit: 50 }),
    enabled: userRole === "provider",
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 60_000,
  });

  return {
    activeProjectQuery,
    providersQuery,
    vendorsQuery,
    projectsQuery,
    providerManproProjectsQuery,
  };
}
